// src/components/common/Button.tsx
import React from 'react';
import styled, { css } from 'styled-components';

type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'outline' | 'text';
type ButtonSize = 'small' | 'medium' | 'large';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  icon?: React.ReactNode;
  loading?: boolean;
}

const ButtonBase = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.short};
  border: none;
  outline: none;
  gap: 8px;
  
  ${({ fullWidth }) => fullWidth && css`
    width: 100%;
  `}
  
  ${({ disabled }) => disabled && css`
    opacity: 0.6;
    cursor: not-allowed;
  `}
  
  ${({ size, theme }) => {
    switch (size) {
      case 'small':
        return css`
          padding: ${theme.spacing(0.75)} ${theme.spacing(2)};
          font-size: 0.8125rem;
        `;
      case 'large':
        return css`
          padding: ${theme.spacing(1.5)} ${theme.spacing(4)};
          font-size: 1rem;
        `;
      default: // medium
        return css`
          padding: ${theme.spacing(1.25)} ${theme.spacing(3)};
          font-size: 0.875rem;
        `;
    }
  }}
  
  ${({ variant, theme }) => {
    switch (variant) {
      case 'secondary':
        return css`
          background-color: ${theme.colours.secondary.main};
          color: ${theme.colours.secondary.contrastText};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colours.secondary.dark};
          }
        `;
      case 'outline':
        return css`
          background-color: transparent;
          color: ${theme.colours.primary.main};
          border: 1px solid ${theme.colours.primary.main};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colours.primary.main}10;
          }
        `;
      case 'text':
        return css`
          background-color: transparent;
          color: ${theme.colours.primary.main};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colours.primary.main}10;
          }
        `;
      default: // primary - matches the dark purple "Add Client" button
        return css`
          background-color: ${theme.colours.primary.main};
          color: ${theme.colours.primary.contrastText};
          border: 1px solid ${theme.colours.primary.main};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colours.primary.light};
          }
        `;
    }
  }}
`;

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  icon,
  loading,
  type = 'button',
  ...props
}) => {
  return (
    <ButtonBase
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      type={type}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading ? 'Loading...' : (
        <>
          {icon && icon}
          {children}
        </>
      )}
    </ButtonBase>
  );
};

export default Button;